import React from "react";
import type { Editor } from "@tiptap/react";

import { getAiChangesStorage, getAiStorage } from "containers/Tiptap/lib/ai-utils";

/**
 * Auto-clean AI storage when the doc has been reverted (e.g. via undo) back
 * to the pre-AI state. Detects `storage.ai.response` still set with no
 * tracked AI changes remaining, and runs `aiAccept` to clear the pending
 * state. Prevents the Accept/Reject dialog from re-appearing on subsequent
 * edits to a doc the user has already undone past the AI generation.
 */
export const useAutoCleanStaleAi = (editor: Editor | null): void => {
  React.useEffect(() => {
    if (!editor) return;

    let pending: number | null = null;

    const tryClean = (): void => {
      pending = null;
      if (editor.isDestroyed) return;
      const aiStorage = getAiStorage(editor);
      if (!aiStorage || aiStorage.state !== "idle" || aiStorage.response === undefined) return;

      const changes = getAiChangesStorage(editor)?.getChanges() ?? [];
      if (changes.length > 0) return;

      editor
        .chain()
        .aiAccept()
        .command(({ commands }) => commands.stopTrackingAiChanges())
        .run();
    };

    const handleUpdate = (): void => {
      // Defer to the next tick so aiChanges' plugin state has finished
      // applying this transaction before we read getChanges(). Coalesce
      // bursts of updates into a single check.
      if (pending !== null) return;
      pending = window.setTimeout(tryClean, 0);
    };

    editor.on("update", handleUpdate);
    return () => {
      editor.off("update", handleUpdate);
      if (pending !== null) window.clearTimeout(pending);
    };
  }, [editor]);
};
